import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useTrainerSessions } from "../../lib/queries";
import { Empty, ErrorBox, Loading } from "../../components/Loading";
import { dateKey, fmtDayLabel, fmtTimeRange } from "../../lib/format";

export default function TrainerCalendar() {
  const { user } = useAuth();
  const [scope, setScope] = useState<"mine" | "club">("mine");
  const q = useTrainerSessions(user?.trainer_id ?? undefined, scope);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox message={(q.error as Error).message} />;

  const sessions = (q.data ?? []).filter((s) => s.status !== "cancelled");
  const upcoming = sessions.filter((s) => new Date(s.starts_at) >= new Date(Date.now() - 60_000));
  const past = sessions.filter((s) => new Date(s.starts_at) < new Date(Date.now() - 60_000));

  const groups = new Map<string, typeof upcoming>();
  for (const s of upcoming) {
    const k = dateKey(s.starts_at);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(s);
  }

  return (
    <div className="p-4 space-y-4">
      <header>
        <h1 className="text-2xl font-bold">{scope === "mine" ? "Мої заняття" : "Календар клубу"}</h1>
      </header>

      <div className="inline-flex rounded-full bg-tg-secondary p-0.5 text-sm">
        <button
          className={`px-3 py-1 rounded-full transition ${scope === "mine" ? "bg-tg-bg shadow" : "text-tg-hint"}`}
          onClick={() => setScope("mine")}
        >Мої</button>
        <button
          className={`px-3 py-1 rounded-full transition ${scope === "club" ? "bg-tg-bg shadow" : "text-tg-hint"}`}
          onClick={() => setScope("club")}
        >Усі клубу</button>
      </div>

      <Link to="/t/new" className="btn-primary w-full block text-center">+ Створити заняття</Link>

      {upcoming.length === 0 ? (
        <Empty label={scope === "mine" ? "У тебе поки нічого не заплановано." : "У клубі поки нічого не заплановано."} />
      ) : (
        Array.from(groups.entries()).map(([k, list]) => (
          <section key={k}>
            <h2 className="text-tg-hint text-sm uppercase tracking-wide px-1 mb-2">
              {fmtDayLabel(list[0].starts_at)}
            </h2>
            <div className="space-y-2">
              {list.map((s) => {
                const mine = s.trainer.id === user?.trainer_id;
                return (
                  <Link key={s.id} to={`/t/session/${s.id}`} className={`card block ${mine ? "" : "opacity-70"}`}>
                    <div className="flex justify-between items-baseline">
                      <div className="font-semibold">{s.activity_type.emoji} {s.activity_type.name}</div>
                      <div className="text-tg-hint text-sm tabular-nums">
                        {fmtTimeRange(s.starts_at, s.ends_at)}
                      </div>
                    </div>
                    {s.location_text && (
                      <div className="text-tg-hint text-sm mt-1">📍 {s.location_text}</div>
                    )}
                    <div className="text-tg-hint text-sm mt-1">
                      Тренер: {s.trainer.name} · {s.bookings.filter((b) => b.status !== "cancelled").length}/{s.capacity} записані
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))
      )}

      {past.length > 0 && (
        <section>
          <h2 className="text-tg-hint text-sm uppercase tracking-wide px-1 mb-2 mt-6">Минулі</h2>
          <div className="space-y-2">
            {past.slice(0, 10).map((s) => (
              <Link key={s.id} to={`/t/session/${s.id}`} className="card block opacity-70">
                <div className="flex justify-between">
                  <span>{s.activity_type.emoji} {s.activity_type.name} · {s.trainer.name}</span>
                  <span className="text-tg-hint text-sm">{fmtDayLabel(s.starts_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
